import { useState, useRef, useCallback, useEffect } from "react";
import { Loader } from "@/components/shared";
import SEOComponent from "@/components/shared/SEOComponent";
import { useOutletContext } from "react-router-dom";
import {
  Camera,
  Heart,
  Share,
  IndianRupee,
  Play,
  Volume2,
  VolumeX,
  X,
  Plus,
  AlertTriangle,
  CheckCircle,
  StopCircle,
  RotateCcw,
  Send,
  Bookmark,
  BookmarkCheck,
  Upload,
  Video,
  Info,
  GraduationCap,
  Store,
  TrendingUp,
  Laptop,
  User,
  Zap,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useUserContext } from "@/context/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

// Import the new bizz queries
import {
  useGetVideoFeed,
  useCreateVideoPost,
  useUploadVideoFile,
  useVideoInteractionManager,
  QUERY_KEYS,
  useGetVideoTips,
} from "@/bizz/lib/react-query/bizzQueries";

import { getVideoMetadata } from "@/bizz/lib/appwrite/bizzApi";

import {
  uploadFile,
  getFileView,
  deleteFile,
  getKYCStatus,
} from "@/lib/appwrite/api";
import { useQueryClient } from "@tanstack/react-query";
import { useVideoTipPayment } from "@/bizz/lib/cashfree/videoTipPayment";
import { appwriteConfig, databases } from "@/lib/appwrite/config";
import { Query } from "appwrite";
import { KYCVerificationModal } from "@/modals/KYCVerificationModal";
import { validateAndOptimizeVideo } from "@/bizz/lib/utils/videoUtils";

interface OutletContextType {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}

interface VideoPost {
  $id: string;
  videoUrl: string;
  title: string;
  description: string;
  creator: {
    $id: string;
    name: string;
    username: string;
    imageUrl: string;
  };
  stats?: {
    likes: number;
    shares: number;
    tips: number;
    saves: number;
    views: number;
  };
  // Make these optional since they're not in your DB response
  isLiked?: boolean;
  isSaved?: boolean;
  duration?: number; // Also make this optional
  createdAt: string;
  category: string;
  isActivated: boolean;
  individual: string;
}

// Video Post Validation Schema
const VideoPostValidation = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title too long"),
  description: z.string().max(500, "Description too long"),
  category: z.string().min(1, "Category is required"),
  file: z
    .array(z.instanceof(File))
    .min(1, "Video file is required")
    .max(1, "Only one video file allowed"),
});

const ENHANCED_BUSINESS_CATEGORIES = [
  // Keep your existing categories with enhanced descriptions
  {
    value: "business",
    label: "Business Showcase",
    description: "Promote your products, services, or local business",
    examples:
      "Bakery items, salon services, café specials, tailor work, electrician services",
    placeholder: "Show your products/services in action...",
    icon: Store,
    colorClass: "text-blue-500",
  },
  {
    value: "skills",
    label: "Skill Tutorials",
    description: "Teach valuable skills people can learn and apply",
    examples:
      "Cooking techniques, painting tutorials, makeup artistry, stitching methods, repair work",
    placeholder: "Share your expertise and teach others...",
    icon: GraduationCap,
    colorClass: "text-green-500",
  },
  {
    value: "marketing",
    label: "Marketing & Growth",
    description: "Marketing strategies and business growth tips",
    examples:
      "Social media marketing, customer acquisition, growth hacks, branding tips",
    placeholder: "Share your marketing wisdom and growth strategies...",
    icon: TrendingUp,
    colorClass: "text-purple-500",
  },
  {
    value: "finance",
    label: "Finance & Strategy",
    description: "Financial advice and business planning",
    examples:
      "Cost-saving tips, investment advice, pricing strategies, financial planning",
    placeholder: "Share financial insights and business strategies...",
    icon: IndianRupee,
    colorClass: "text-emerald-500",
  },
  {
    value: "technology",
    label: "Business Technology",
    description: "Tech tools and digital solutions for business",
    examples:
      "POS systems, billing software, social media tools, automation setup",
    placeholder: "Demonstrate tech tools that help businesses...",
    icon: Laptop,
    colorClass: "text-cyan-500",
  },
  {
    value: "entrepreneurship",
    label: "Entrepreneur Stories",
    description: "Real business journeys and startup experiences",
    examples:
      "Startup struggles, growth milestones, failure lessons, success stories",
    placeholder: "Tell your entrepreneurial story and experiences...",
    icon: User,
    colorClass: "text-orange-500",
  },

  // Add strategic new categories for future growth
  {
    value: "before-after",
    label: "Transformation Results",
    description: "Show before/after results of your services",
    examples:
      "Home cleaning, car detailing, beauty transformations, packaging design",
    placeholder: "Showcase your amazing transformations and results...",
    icon: Zap,
    colorClass: "text-yellow-500",
  },
  {
    value: "professional-training",
    label: "Professional Training",
    description: "Coaching, training, and consulting services",
    examples:
      "Sales training, language coaching, design mentoring, skill development",
    placeholder: "Demonstrate your training/coaching expertise...",
    icon: Award,
    colorClass: "text-pink-500",
  },
];

// 3. KEEP YOUR EXISTING CONTENT GUIDELINES (or enhance them)
const CONTENT_GUIDELINES = {
  allowed: [
    "Business tutorials and how-to content",
    "Product/service demonstrations and showcases",
    "Professional skill teaching and training",
    "Entrepreneurship advice and real stories",
    "Marketing strategies and growth tips",
    "Technology tools for business growth",
    "Financial advice and business planning",
    "Professional transformations and results",
  ],
  notAllowed: [
    "Entertainment-only content",
    "Dance/music videos (unless for events business)",
    "Personal vlogs unrelated to business",
    "Inappropriate or adult content",
    "Pure comedy/memes without business value",
    "Personal relationships/drama",
    "Non-educational lifestyle content",
    "Gaming content (unless business-related)",
  ],
};

const Bizz = () => {
  const { user } = useUserContext();
  const { selectedCategory } = useOutletContext<OutletContextType>();
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string>("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>("");
  const [videoDuration, setVideoDuration] = useState<number>(0);

  // Tip modal state
  const [showTipModal, setShowTipModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoPost | null>(null);
  const [tipAmount, setTipAmount] = useState(10);
  const [tipMessage, setTipMessage] = useState("");

  const [videoInteractionStates, setVideoInteractionStates] = useState<{
    [videoId: string]: {
      isLiked: boolean;
      isSaved: boolean;
      tempLikes: number;
      tempSaves: number;
    };
  }>({});

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const recordingVideoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [uploadStage, setUploadStage] = useState<string>("");
  const queryClient = useQueryClient();
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [kycVerified, setKycVerified] = useState(false);
  const [loadingKYC, setLoadingKYC] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(new Set());
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useEffect(() => {
    const checkKYCStatus = async () => {
      if (!user?.id) return;

      try {
        const checkExistingKYC = await getKYCStatus(user.id);
        setKycVerified(
          checkExistingKYC.panVerified && checkExistingKYC.bankVerified
        );
      } catch (error) {
        console.error("Failed to fetch KYC status:", error);
      } finally {
        setLoadingKYC(false);
      }
    };

    checkKYCStatus();
  }, [user?.id]);
  const { handleTipPayment, isProcessing: isPaymentLoading } =
    useVideoTipPayment();
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);

  const getCategoryInfo = (categoryValue: string) => {
    return (
      ENHANCED_BUSINESS_CATEGORIES.find((cat) => cat.value === categoryValue) ||
      ENHANCED_BUSINESS_CATEGORIES[0]
    );
  };

  const getCategoryPlaceholder = (
    categoryValue: string,
    field: "title" | "description"
  ) => {
    const category = getCategoryInfo(categoryValue);
    if (field === "title") {
      return `${category.label}: Add a catchy title...`;
    }
    return category.placeholder;
  };

  // Form for hybrid upload approach
  const form = useForm<z.infer<typeof VideoPostValidation>>({
    resolver: zodResolver(VideoPostValidation),
    defaultValues: {
      title: "",
      description: "",
      category: "business",
      file: [],
    },
  });

  // Use the new bizz video feed query
  const {
    data: feedData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetVideoFeed(selectedCategory);

  // Video interaction hooks
  const videoInteractionManager = useVideoInteractionManager(user?.id || "");

  // Upload hooks
  const uploadVideoFile = useUploadVideoFile();
  const createVideoPost = useCreateVideoPost();

  // Get videos from feed
  const allVideos = feedData?.pages?.flatMap((page) => page.documents) ?? [];

  const filtered = allVideos.filter((video: any) => {
    return (
      video?.isActivated === true &&
      video?.individual === "bizz" &&
      typeof video?.videoUrl === "string" &&
      typeof video?.title === "string" &&
      video?.creator // Ensure creator exists
    );
  });

  // Force cast only after filtering and minimal shape validation
  const videos = filtered as unknown as VideoPost[];

  // Infinite scroll observer
  const lastVideoRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // File upload handlers
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ✅ Show loading state
    setIsUploading(true);
    setUploadStage("Validating video...");

    try {
      // ✅ Validate and optimize the video
      const { isValid, optimizedFile, error } =
        await validateAndOptimizeVideo(file);

      if (!isValid) {
        toast({
          title: "Invalid File",
          description: error || "Please select a valid video file",
          variant: "destructive",
        });
        setIsUploading(false);
        return;
      }

      // ✅ Use the optimized file
      setSelectedFile(optimizedFile);
      const previewUrl = URL.createObjectURL(optimizedFile);
      setFilePreviewUrl(previewUrl);
      setShowUploadModal(true);

      // Set form values
      form.setValue("file", [optimizedFile]);

      setUploadStage("Getting video info...");

      // Get video duration
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = Math.floor(video.duration);
        if (duration > 30) {
          toast({
            title: "Video Too Long",
            description: `Video duration is ${duration}s. Maximum allowed is 30 seconds.`,
            variant: "destructive",
          });

          // Clear the selected file
          setSelectedFile(null);
          URL.revokeObjectURL(previewUrl);
          setFilePreviewUrl("");
          setShowUploadModal(false);
          form.setValue("file", []);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          URL.revokeObjectURL(video.src);
          setIsUploading(false);
          return;
        }
        setVideoDuration(duration);
        URL.revokeObjectURL(video.src);
        setIsUploading(false);
        setUploadStage("");
      };

      video.onerror = () => {
        toast({
          title: "Invalid Video",
          description: "Could not read video file. Please try another file.",
          variant: "destructive",
        });

        // Clear the selected file
        setSelectedFile(null);
        URL.revokeObjectURL(previewUrl);
        setFilePreviewUrl("");
        setShowUploadModal(false);
        form.setValue("file", []);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        URL.revokeObjectURL(video.src);
        setIsUploading(false);
        setUploadStage("");
      };

      video.src = previewUrl;
    } catch (error) {
      console.error("File processing error:", error);
      toast({
        title: "Processing Error",
        description: "Failed to process video file. Please try again.",
        variant: "destructive",
      });
      setIsUploading(false);
      setUploadStage("");
    }
  };

  // Start camera for recording
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 720 },
          height: { ideal: 1280 },
        },
        audio: true,
      });

      streamRef.current = stream;
      if (recordingVideoRef.current) {
        recordingVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  // Start recording
  const startRecording = () => {
    if (!streamRef.current) return;

    try {
      // Better mobile support
      const options = {
        mimeType: "video/webm;codecs=vp8", // VP8 has better mobile support than VP9
        videoBitsPerSecond: 1000000, // 1Mbps - lower for mobile
      };

      // Fallback for iOS/Safari
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = "video/mp4"; // Fallback
      }

      const mediaRecorder = new MediaRecorder(streamRef.current, options);

      // Rest of your recording logic stays the same...
      const chunks = [] as any;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: options.mimeType });

        // ✅ Check if recorded video needs compression
        const fileSizeMB = blob.size / (1024 * 1024);

        if (fileSizeMB > 100) {
          toast({
            title: "Recording Too Large",
            description: `Recording is ${fileSizeMB.toFixed(1)}MB. Try recording for less time.`,
            variant: "destructive",
          });

          // Reset recording
          setRecordedBlob(null);
          setRecordedUrl("");
          startCamera();
          return;
        }

        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stopCamera();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 30) {
            stopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast({
        title: "Recording Error",
        description: "Failed to start recording. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  // Flip camera
  const flipCamera = async () => {
    stopCamera();
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    await startCamera();
  };

  // Reset recording
  const resetRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl("");
    setRecordingTime(0);
    startCamera();
  };

  // Hybrid upload handler using existing createPost pattern
  const handleHybridUpload = async () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to upload videos",
        variant: "destructive",
      });
      return;
    }

    const videoSource = recordedBlob || selectedFile;
    const title = form.getValues("title");
    const description = form.getValues("description");
    const category = form.getValues("category");

    if (!videoSource) {
      toast({
        title: "Missing Video",
        description: "Please record a video or select a file to upload",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Missing Title",
        description: "Please add a title for your video",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStage("Preparing upload...");

    let uploadedFiles: any[] = [];
    let fileUrls: string[] = [];

    try {
      // Create file from blob or use selected file
      let videoFile: File;
      if (recordedBlob) {
        videoFile = new File([recordedBlob], `video_${Date.now()}.webm`, {
          type: "video/webm",
        });
      } else {
        videoFile = selectedFile!;
      }

      const fileSizeMB = videoFile.size / (1024 * 1024);
      if (fileSizeMB > 100) {
        throw new Error(
          `Video size (${fileSizeMB.toFixed(1)}MB) exceeds 100MB limit`
        );
      }
      setUploadProgress(5);
      setUploadStage("Uploading video...");

      // Upload file using existing uploadFile function (like in createPost)
      const uploadedFile = await uploadFile(videoFile);
      if (!uploadedFile) {
        throw new Error(`Failed to upload video file`);
      }

      // Get file URL for video (use getFileView for videos)
      const fileUrl = getFileView(uploadedFile.$id);
      if (!fileUrl) {
        await deleteFile(uploadedFile.$id);
        throw new Error(`Failed to get URL for video file`);
      }

      uploadedFiles.push(uploadedFile);
      fileUrls.push(fileUrl);

      setUploadProgress(90);
      setUploadStage("Getting video metadata...");

      // Get video duration
      let duration = 0;
      try {
        const metadata = await getVideoMetadata(videoFile);
        duration = metadata.duration;
      } catch (error) {
        console.warn("Failed to get video metadata:", error);
      }

      setUploadProgress(95);
      setUploadStage("Creating post...");

      // Create video post document (similar to createPost structure)
      const newVideoPost = await createVideoPost.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        category: category.toLowerCase().trim(),
        videoUrl: fileUrl,
        duration: duration,
        creatorId: user.id,
        individual: "bizz",
        isActivated: true,
      });

      if (!newVideoPost) {
        // Clean up uploaded files if post creation fails
        for (const uploadedFile of uploadedFiles) {
          try {
            await deleteFile(uploadedFile.$id);
          } catch (deleteError) {
            console.error(
              `Error deleting file ${uploadedFile.$id}:`,
              deleteError
            );
          }
        }
        throw new Error("Failed to create video post");
      }

      setUploadProgress(100);
      setUploadStage("Upload complete!");

      toast({
        title: "Video Uploaded Successfully!",
        description: `Your ${fileSizeMB.toFixed(1)}MB video has been uploaded and processed.`,
      });

      // Reset form and state
      form.reset();
      if (recordedBlob) {
        resetRecording();
      }
      setShowRecordModal(false);
      setShowUploadModal(false);
      setUploadProgress(0);
      setUploadStage("");
    } catch (error) {
      console.error("Hybrid upload failed:", error);
      setUploadStage("Upload failed");

      // Clean up on error
      for (const uploadedFile of uploadedFiles) {
        try {
          await deleteFile(uploadedFile.$id);
        } catch (deleteError) {
          console.error(
            `Error deleting file ${uploadedFile.$id}:`,
            deleteError
          );
        }
      }

      toast({
        title: "Upload Failed",
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle video interactions with real APIs
  const handleVideoLike = async (videoId: string) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to like videos",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (!currentVideo) return;

      const currentState = videoInteractionStates[videoId];
      const isCurrentlyLiked = currentState?.isLiked ?? currentVideo.isLiked;
      const currentLikes =
        currentState?.tempLikes ?? (currentVideo.stats?.likes || 0);

      // Optimistic update - immediately update UI
      setVideoInteractionStates((prev) => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          isLiked: !isCurrentlyLiked,
          tempLikes: !isCurrentlyLiked
            ? currentLikes + 1
            : Math.max(0, currentLikes - 1),
          isSaved: currentState?.isSaved ?? currentVideo.isSaved,
          tempSaves:
            currentState?.tempSaves ?? (currentVideo.stats?.saves || 0),
        },
      }));
      await videoInteractionManager.handleLike(videoId);

      // Force refresh the specific video's stats
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
      });

      // Also refresh the feed to get updated interaction states
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
    } catch (error) {
      console.error("Error liking video:", error);
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (currentVideo) {
        setVideoInteractionStates((prev) => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            isLiked: currentVideo.isLiked || false,  // ✅ Handle undefined
            tempLikes: currentVideo.stats?.likes || 0,
          },
        }));
      }

      toast({
        title: "Error",
        description: "Failed to like video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVideoSave = async (videoId: string) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to save videos",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentVideo = videos.find((v) => v.$id === videoId);
      if (!currentVideo) return;

      const currentState = videoInteractionStates[videoId];
      const isCurrentlySaved = currentState?.isSaved ?? currentVideo.isSaved;
      const currentSaves =
        currentState?.tempSaves ?? (currentVideo.stats?.saves || 0);

      // Optimistic update - immediately update UI
      setVideoInteractionStates((prev) => ({
        ...prev,
        [videoId]: {
          ...prev[videoId],
          isSaved: !isCurrentlySaved,
          tempSaves: !isCurrentlySaved
            ? currentSaves + 1
            : Math.max(0, currentSaves - 1),
          isLiked: currentState?.isLiked ?? currentVideo.isLiked,
          tempLikes:
            currentState?.tempLikes ?? (currentVideo.stats?.likes || 0),
        },
      }));
      await videoInteractionManager.handleSave(videoId);

      // Force refresh the specific video's stats
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
      });

      // Also refresh the feed to get updated interaction states
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
      });
    } catch (error) {
      console.error("Error saving video:", error);

      const currentVideo = videos.find((v) => v.$id === videoId);
      if (currentVideo) {
        setVideoInteractionStates((prev) => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            isSaved: currentVideo.isSaved || false,  // ✅ Handle undefined
            tempSaves: currentVideo.stats?.saves || 0,
          },
        }));
      }

      toast({
        title: "Error",
        description: "Failed to save video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVideoShare = async (videoId: string) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Check out this business video",
          url: `${window.location.origin}/bizz/video/${videoId}`,
        });
      } else {
        await navigator.clipboard.writeText(
          `${window.location.origin}/bizz/video/${videoId}`
        );
        toast({
          title: "Link Copied!",
          description: "Video link copied to clipboard",
        });
      }

      // Track share interaction
      if (user?.id) {
        await videoInteractionManager.handleShare(videoId);

        // Force refresh the specific video's stats
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_STATS, videoId],
        });

        // Also refresh the feed to get updated interaction states
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
        });
      }
    } catch (error) {
      console.error("Error sharing video:", error);
    }
  };

  const handleTipClick = (video: VideoPost) => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to send tips",
        variant: "destructive",
      });
      return;
    }
    setSelectedVideo(video);
    setShowTipModal(true);
  };

  const handleSendTip = async () => {
    if (!selectedVideo || !user?.id) return;

    try {
      await handleTipPayment(
        user.id,
        selectedVideo.creator.$id,
        selectedVideo.$id,
        tipAmount,
        {
          name: user.name,
          emailOrPhone: user.emailOrPhone,
        },
        () => {
          setShowTipModal(false);
          setSelectedVideo(null);
          setTipAmount(10);
          setTipMessage("");

          // Refresh the specific video's tip data
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_VIDEO_TIPS, selectedVideo.$id],
          });

          // Also refresh general stats
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_VIDEO_STATS, selectedVideo.$id],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_VIDEO_FEED],
          });
        }
      );
    } catch (error) {
      console.error("Error in tip payment:", error);
    }
  };

  // Video playback controls
  const togglePlayPause = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const handleUploadClick = () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to upload videos",
        variant: "destructive",
      });
      return;
    }
    if (!kycVerified) {
      setShowKYCModal(true);
      return;
    }

    fileInputRef.current?.click();
  };

  const handleRecordClick = () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to record videos",
        variant: "destructive",
      });
      return;
    }

    if (!kycVerified) {
      setShowKYCModal(true);
      return;
    }

    setShowRecordModal(true);
  };

  const VideoTipButton = ({ video }: { video: VideoPost }) => {
    const { data: tipData } = useGetVideoTips(video.$id);
    return (
      <div className="flex flex-col items-center relative z-50">
        <Button
          onClick={() => handleTipClick(video)}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-2.5 md:p-3 shadow-lg transition-all duration-200 border border-white/10 relative z-50">
          <IndianRupee className="h-5 w-5 md:h-6 md:w-6" />
        </Button>
        <div className="text-center mt-1 relative z-50">
          {tipData && tipData.total > 0 && (
            <span className="text-green-400 text-xs font-bold bg-black/50 px-2 py-0.5 rounded-full block">
              ₹{tipData.total}
            </span>
          )}
        </div>
      </div>
    );
  };
  // Initialize camera when modal opens
  useEffect(() => {
    if (showRecordModal && !recordedBlob) {
      startCamera();
    }

    return () => {
      if (showRecordModal) {
        stopCamera();
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }
      }
    };
  }, [showRecordModal]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
      stopCamera();
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Set up video intersection observers when videos change
  useEffect(() => {
    const observerOptions = {
      threshold: isMobile ? 0.3 : 0.5,
      rootMargin: isMobile ? "0px 0px -50px 0px" : "0px 0px -50px 0px", // ✅ Fixed mobile rootMargin
    };

    const handleIntersection = (entries: any) => {
      entries.forEach((entry: any) => {
        const video = entry.target;
        const videoIndex = parseInt(video.dataset.index || "0");

        if (entry.isIntersecting) {
          // ✅ Ensure the first video gets proper treatment
          videoRefs.current.forEach((v, i) => {
            if (v && i !== videoIndex) {
              v.pause();
              v.currentTime = 0;
            }
          });

          const playVideo = async () => {
            try {
              video.muted = true;
              await video.play();
              setCurrentVideoIndex(videoIndex);
              setIsPlaying(true);
            } catch (error) {
              console.log("Autoplay failed, retrying...", error);
              setTimeout(async () => {
                try {
                  video.muted = true;
                  await video.play();
                  setCurrentVideoIndex(videoIndex);
                  setIsPlaying(true);
                } catch (retryError) {
                  console.log("Retry failed:", retryError);
                  setIsPlaying(false);
                }
              }, 500);
            }
          };

          playVideo();
        } else {
          video.pause();
          if (videoIndex === currentVideoIndex) {
            setIsPlaying(false);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions
    );

    // ✅ Ensure first video is properly handled on mobile
    setTimeout(() => {
      videoRefs.current.forEach((video, index) => {
        if (video) {
          observer.observe(video);
          // ✅ Manually trigger first video on mobile
          if (index === 0 && isMobile) {
            setCurrentVideoIndex(0);
          }
        }
      });
    }, 100);

    return () => observer.disconnect();
  }, [videos.length, currentVideoIndex, isMobile]);

  useEffect(() => {
    const loadInteractions = async () => {
      if (!videos || videos.length === 0 || !user?.id) return;

      try {
        const newStates: any = {};

        // Get all video IDs
        const videoIds = videos.map((v) => v.$id);

        // Fetch user interactions for all videos at once
        const result = await databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.videoInteractionsCollectionId,
          [
            Query.equal("videoId", videoIds),
            Query.equal("userId", user.id),
            Query.equal("type", ["like", "save"]),
          ]
        );

        // Create map of interactions
        const interactions = new Map();
        result.documents.forEach((doc) => {
          interactions.set(`${doc.videoId}-${doc.type}`, true);
        });

        // Set state for each video
        videos.forEach((video) => {
          if (!videoInteractionStates[video.$id]) {
            newStates[video.$id] = {
              isLiked: interactions.has(`${video.$id}-like`),
              isSaved: interactions.has(`${video.$id}-save`),
              tempLikes: video.stats?.likes || 0,
              tempSaves: video.stats?.saves || 0,
            };
          }
        });

        if (Object.keys(newStates).length > 0) {
          setVideoInteractionStates((prev) => ({ ...prev, ...newStates }));
        }
      } catch (error) {
        console.error("Error loading interactions:", error);
        // Set default states
        const defaultStates: any = {};
        videos.forEach((video) => {
          if (!videoInteractionStates[video.$id]) {
            defaultStates[video.$id] = {
              isLiked: false,
              isSaved: false,
              tempLikes: video.stats?.likes || 0,
              tempSaves: video.stats?.saves || 0,
            };
          }
        });
        if (Object.keys(defaultStates).length > 0) {
          setVideoInteractionStates((prev) => ({ ...prev, ...defaultStates }));
        }
      }
    };

    loadInteractions();
  }, [videos.length, user?.id]);

  // ✅ Replace both the cleanupResources function AND the useEffect with this single useEffect:
  useEffect(() => {
    return () => {
      // Clean up video URLs
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }

      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Clear intervals
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }

      // Disconnect observers
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [recordedUrl, filePreviewUrl]);

  // Error state - must be after all hooks
  if (isError) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something went wrong</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOComponent
        title="Bizz - Showcase Business Skills & Earn Tips | BUSICONN Short Videos"
        description="Create and share Bizz short videos to showcase your business skills, product knowledge, and professional expertise. Earn tips from engaged viewers while building your brand on BUSICONN's social commerce platform. Upload before-after transformations, tutorials, and skill demonstrations."
        canonicalUrl="https://busiconn.com/bizz"
        keywords="business skills videos, professional tutorials, skill showcase platform, earn tips online, business knowledge sharing, product demonstration videos, professional content creation, before after videos, business expertise platform"
        ogType="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Bizz - Business Skills Showcase",
          description:
            "Platform for sharing business skills through short videos and earning tips from viewers",
          url: "https://busiconn.com/bizz",
          isPartOf: {
            "@type": "WebSite",
            name: "BUSICONN",
            url: "https://busiconn.com",
          },
          mainEntity: {
            "@type": "VideoGallery",
            name: "Bizz Business Skills Videos",
            description:
              "Collection of short videos showcasing business skills and professional expertise",
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://busiconn.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Bizz",
                item: "https://busiconn.com/bizz",
              },
            ],
          },
        }}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Main Video Feed */}
      <div className="h-screen w-full relative overflow-hidden bg-black">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2">
            <h1 className="text-white font-bold text-xl">Bizz</h1>
            <Button
              onClick={() => setShowGuidelinesModal(true)}
              className="text-white/70 hover:text-white p-1"
              variant="ghost">
              <Info className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleUploadClick}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2">
              <Upload className="h-5 w-5" />
            </Button>
            <Button
              onClick={handleRecordClick}
              className="bg-primary-500 hover:bg-primary-600 text-white rounded-full p-2">
              <Plus className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Video Container */}
        <div
          ref={containerRef}
          className="h-full w-full relative overflow-y-scroll"
          style={{
            scrollSnapType: "y mandatory",
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            // ✅ Prevent video stretching
            maxWidth: "100vw",
            maxHeight: "100vh",
          }}>
          {videos.map((video, index) => {
            const interactionState = videoInteractionStates[video.$id];
            const isLiked = interactionState?.isLiked ?? (video.isLiked || false);
            const isSaved = interactionState?.isSaved ?? (video.isSaved || false);

            // Use optimistic stats if available
            const stats = {
              likes: interactionState?.tempLikes ?? (video.stats?.likes || 0),
              shares: video.stats?.shares || 0,
              saves: interactionState?.tempSaves ?? (video.stats?.saves || 0),
              tips: video.stats?.tips || 0,
              views: video.stats?.views || 0,
            };
            return (
              <div
                key={video.$id}
                ref={index === videos.length - 1 ? lastVideoRef : null}
                className="h-full w-full relative snap-start flex items-center justify-center bg-black"
                style={{
                  minHeight: "100vh",
                  maxHeight: "100vh",
                  // ✅ Prevent overflow
                  overflow: "hidden",
                }}>
                {/* Desktop/Laptop Video Container with proper aspect ratio */}
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* Video with responsive sizing */}
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                      if (el) {
                        el.dataset.index = index.toString();
                        el.dataset.videoId = video.$id;
                      }
                    }}
                    src={video.videoUrl}
                    className="w-full h-full object-cover md:max-w-md md:max-h-[80vh] md:rounded-lg"
                    loop
                    muted={true} // ✅ Force muted for autoplay
                    playsInline // ✅ Critical for mobile
                    preload="metadata"
                    controls={false}
                    autoPlay={false} // ✅ Don't use autoPlay attribute
                    onClick={(e) => {
                      // ✅ Prevent click interference on mobile
                      if (e.target === e.currentTarget) {
                        togglePlayPause(index);
                      }
                    }}
                    onLoadedData={() => {
                      // ✅ Better autoplay logic
                      if (index === currentVideoIndex) {
                        const video = videoRefs.current[index];
                        if (video) {
                          video.muted = true; // Ensure muted before play
                          video.play().catch(() => {
                            console.log("Autoplay failed for video", index);
                          });
                        }
                      }
                    }}
                    onLoadStart={() => {
                      setLoadingVideos((prev) => new Set(prev).add(video.$id));
                    }}
                    onCanPlay={() => {
                      setLoadingVideos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(video.$id);
                        return newSet;
                      });
                    }}
                    onError={() => {
                      console.error("Video failed to load:", video.videoUrl);
                      setLoadingVideos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(video.$id);
                        return newSet;
                      });
                    }}
                    style={{
                      minHeight: "100vh",
                      maxHeight: "100vh",
                      objectFit: "cover",
                      objectPosition: "center",
                      pointerEvents: "auto",
                    }}
                  />
                  {loadingVideos.has(video.$id) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader />
                    </div>
                  )}

                  {/* Black bars/background for desktop to maintain mobile-first feel */}
                  <div className="hidden md:block absolute inset-0 bg-black -z-10"></div>
                </div>
                {/* Video Overlay Controls */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {!isPlaying && currentVideoIndex === index && (
                    <Button
                      onClick={() => togglePlayPause(index)}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-4">
                      <Play className="h-8 w-8" />
                    </Button>
                  )}
                </div>
                {/* Creator Info - Responsive positioning */}
                <div className="absolute bottom-16 md:bottom-20 left-4 right-24 md:right-28 lg:right-32 xl:right-36 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={video.creator.imageUrl}
                      alt={video.creator.name}
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-white"
                    />
                    <div>
                      <p className="font-semibold text-sm md:text-base">
                        {video.creator.name}
                      </p>
                      <p className="text-xs md:text-sm text-gray-300">
                        @{video.creator.username}
                      </p>
                    </div>
                  </div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-sm text-gray-200 line-clamp-2 md:line-clamp-3">
                    {video.description}
                  </p>

                  {/* Category Badge */}
                  <div className="mt-2">
                    {(() => {
                      const categoryInfo = getCategoryInfo(video.category);
                      const IconComponent = categoryInfo.icon;
                      return (
                        <div className="inline-flex items-center gap-1 bg-black/60 px-2 py-1 rounded-full">
                          <IconComponent
                            className={`h-3 w-3 ${categoryInfo.colorClass}`}
                          />
                          <span className="text-xs text-gray-300">
                            {categoryInfo.label}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Action Buttons - ENHANCED WITH BETTER VISUAL STATES */}
                <div
                  className="absolute right-2 md:right-4 lg:right-6 xl:right-8 top-1/2 transform -translate-y-1/2 flex flex-col gap-3 z-50"
                  style={{
                    pointerEvents: "auto",
                    position: "absolute",
                    zIndex: 9999,
                  }}>
                  {/* Tip - Most prominent at top */}
                  <VideoTipButton video={video} />

                  {/* Like - PROFESSIONAL */}
                  <div className="flex flex-col items-center relative z-50">
                    <Button
                      onClick={() => handleVideoLike(video.$id)}
                      disabled={videoInteractionManager.isLiking}
                      className={`rounded-full p-2.5 md:p-3 shadow-lg transition-all duration-200 relative z-50 ${
                        isLiked
                          ? "bg-rose-500 hover:bg-rose-600"
                          : "bg-black/50 hover:bg-black/70"
                      } text-white border border-white/10`}>
                      <Heart
                        className={`h-5 w-5 md:h-6 md:w-6 ${isLiked ? "fill-white" : ""}`}
                      />
                    </Button>
                    <span
                      className={`text-white text-xs mt-1 font-medium px-2 py-0.5 rounded-full relative z-50 ${
                        isLiked ? "bg-rose-500/50" : "bg-black/50"
                      }`}>
                      {stats.likes > 999
                        ? `${(stats.likes / 1000).toFixed(1)}k`
                        : stats.likes}
                    </span>
                  </div>

                  {/* Save - PROFESSIONAL */}
                  <div className="flex flex-col items-center relative z-50">
                    <Button
                      onClick={() => handleVideoSave(video.$id)}
                      disabled={videoInteractionManager.isSaving}
                      className={`rounded-full p-2.5 md:p-3 shadow-lg transition-all duration-200 relative z-50 ${
                        isSaved
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-black/50 hover:bg-black/70"
                      } text-white border border-white/10`}>
                      {isSaved ? (
                        <BookmarkCheck className="h-5 w-5 md:h-6 md:w-6" />
                      ) : (
                        <Bookmark className="h-5 w-5 md:h-6 md:w-6" />
                      )}
                    </Button>
                    <span
                      className={`text-white text-xs mt-1 font-medium px-2 py-0.5 rounded-full relative z-50 ${
                        isSaved ? "bg-blue-500/50" : "bg-black/50"
                      }`}>
                      {stats.saves}
                    </span>
                  </div>

                  {/* Share */}
                  <div className="flex flex-col items-center relative z-50">
                    <Button
                      onClick={() => handleVideoShare(video.$id)}
                      disabled={videoInteractionManager.isSharing}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 md:p-3 shadow-lg transition-all duration-200 border border-white/10 relative z-50">
                      <Share className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                    <span className="text-white text-xs mt-1 font-medium bg-black/50 px-2 py-0.5 rounded-full relative z-50">
                      {stats.shares}
                    </span>
                  </div>

                  {/* Mute Toggle */}
                  <Button
                    onClick={() => toggleMute(index)}
                    className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 md:p-3 shadow-lg transition-all duration-200 border border-white/10 relative z-50">
                    {isMuted ? (
                      <VolumeX className="h-4 w-4 md:h-5 md:w-5" />
                    ) : (
                      <Volume2 className="h-4 w-4 md:h-5 md:w-5" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Loading More Indicator */}
          {isFetchingNextPage && (
            <div className="h-screen w-full flex items-center justify-center bg-black">
              <Loader />
            </div>
          )}
        </div>

        {/* Record Modal */}
        {showRecordModal && (
          <div className="fixed inset-0 bg-black z-50">
            <div className="h-full w-full relative">
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
                <Button
                  onClick={() => {
                    setShowRecordModal(false);
                    resetRecording();
                    stopCamera();
                  }}
                  className="text-white bg-black/50 hover:bg-black/70 rounded-full p-2">
                  <X className="h-6 w-6" />
                </Button>

                {isRecording && (
                  <div className="bg-rose-500 px-3 py-1 rounded-full">
                    <span className="text-white font-mono text-sm">
                      {Math.floor(recordingTime / 60)}:
                      {(recordingTime % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                )}

                <Button
                  onClick={flipCamera}
                  className="text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
                  disabled={isRecording}>
                  <RotateCcw className="h-6 w-6" />
                </Button>
              </div>

              {/* Video Preview */}
              <div className="h-full w-full flex items-center justify-center">
                {recordedBlob ? (
                  <video
                    src={recordedUrl}
                    controls
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <video
                    ref={recordingVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                )}
              </div>

              {/* Controls */}
              <div className="absolute bottom-20 left-0 right-0 flex justify-center">
                {!recordedBlob ? (
                  <Button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`rounded-full p-6 ${
                      isRecording
                        ? "bg-rose-500 hover:bg-rose-600"
                        : "bg-white hover:bg-gray-200"
                    }`}
                    disabled={!streamRef.current}>
                    {isRecording ? (
                      <StopCircle className="h-8 w-8 text-white" />
                    ) : (
                      <Camera className="h-8 w-8 text-black" />
                    )}
                  </Button>
                ) : (
                  <div className="flex gap-4">
                    <Button
                      onClick={resetRecording}
                      className="bg-gray-500 hover:bg-gray-600 text-white rounded-full px-6 py-3">
                      Retake
                    </Button>
                    <Button
                      onClick={() => setShowRecordModal(false)}
                      className="bg-primary-500 hover:bg-primary-600 text-white rounded-full px-6 py-3">
                      Next
                    </Button>
                  </div>
                )}
              </div>

              {/* Guidelines */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/70 rounded-lg p-3">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-xs font-medium mb-1">
                        Business Content Only
                      </p>
                      <p className="text-gray-300 text-xs">
                        Share business tips, demonstrate skills, showcase
                        products/services, or teach valuable techniques. Choose
                        the right category for better discovery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Modal with Form */}
        {showUploadModal && selectedFile && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 pb-20">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Upload Business Video
                </h2>
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* Video Preview */}
                <video
                  ref={previewVideoRef}
                  src={filePreviewUrl}
                  controls
                  className="w-full rounded-lg bg-black"
                  style={{ maxHeight: "200px" }}
                />

                {/* File Info */}
                <div className="bg-dark-3 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Video className="h-4 w-4" />
                    <span>{selectedFile.name}</span>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-400">
                    <span>
                      Size: {(selectedFile.size / (1024 * 1024)).toFixed(1)}MB
                    </span>
                    {videoDuration > 0 && (
                      <span>
                        Duration: {Math.floor(videoDuration / 60)}:
                        {(videoDuration % 60).toString().padStart(2, "0")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Enhanced Form */}
                <Form {...form}>
                  <form className="space-y-4">
                    {/* Category Selection - FIXED DROPDOWN */}
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Business Category *
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <select
                                {...field}
                                className="w-full bg-dark-3 border border-dark-4 text-white rounded-lg p-3 pr-8 appearance-none cursor-pointer">
                                {ENHANCED_BUSINESS_CATEGORIES.map(
                                  (category) => (
                                    <option
                                      key={category.value}
                                      value={category.value}>
                                      {category.label}
                                    </option>
                                  )
                                )}
                              </select>
                              {/* Custom dropdown arrow */}
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </FormControl>
                          {field.value && (
                            <div className="bg-dark-3 rounded-lg p-2 mt-2">
                              {(() => {
                                const categoryInfo = getCategoryInfo(
                                  field.value
                                );
                                const IconComponent = categoryInfo.icon;
                                return (
                                  <div className="flex items-start gap-2">
                                    <IconComponent
                                      className={`h-4 w-4 ${categoryInfo.colorClass} mt-0.5 flex-shrink-0`}
                                    />
                                    <div>
                                      <p className="text-xs font-medium text-white">
                                        {categoryInfo.label}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {categoryInfo.description}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Examples: {categoryInfo.examples}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Title *
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "title"
                              )}
                              className="bg-dark-3 border-dark-4 text-white"
                              maxLength={100}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/100
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Description
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "description"
                              )}
                              className="bg-dark-3 border-dark-4 text-white min-h-[80px]"
                              maxLength={500}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/500
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    {/* REMOVE THE FileUploader COMPONENT - WE DON'T NEED IT */}
                    {/* The file is already selected via the hidden input */}
                  </form>
                </Form>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{uploadStage}</span>
                      <span className="text-white">
                        {Math.floor(uploadProgress)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.floor(uploadProgress)}%`,
                        }}></div>
                    </div>
                    {uploadStage && (
                      <div className="text-xs text-gray-400">{uploadStage}</div>
                    )}
                  </div>
                )}

                {/* Upload Button */}
                <Button
                  onClick={handleHybridUpload}
                  disabled={
                    !form.watch("title")?.trim() ||
                    isUploading ||
                    uploadVideoFile.status === "loading" ||
                    createVideoPost.status === "loading"
                  }
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white">
                  {isUploading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Business Video
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Post Details Modal (for recorded videos) */}
        {!showRecordModal && recordedBlob && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Post Business Video
                </h2>
                <Button
                  onClick={() => {
                    resetRecording();
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* Video Preview */}
                <video
                  src={recordedUrl}
                  controls
                  className="w-full rounded-lg bg-black"
                  style={{ maxHeight: "200px" }}
                />

                {/* Enhanced Form for recorded video */}
                <Form {...form}>
                  <form className="space-y-4">
                    {/* FIXED Category Selection */}
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Business Category *
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <select
                                {...field}
                                className="w-full bg-dark-3 border border-dark-4 text-white rounded-lg p-3 pr-8 appearance-none cursor-pointer">
                                {ENHANCED_BUSINESS_CATEGORIES.map(
                                  (category) => (
                                    <option
                                      key={category.value}
                                      value={category.value}>
                                      {category.label}
                                    </option>
                                  )
                                )}
                              </select>
                              {/* Custom dropdown arrow */}
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </FormControl>
                          {field.value && (
                            <div className="bg-dark-3 rounded-lg p-2 mt-2">
                              {(() => {
                                const categoryInfo = getCategoryInfo(
                                  field.value
                                );
                                const IconComponent = categoryInfo.icon;
                                return (
                                  <div className="flex items-start gap-2">
                                    <IconComponent
                                      className={`h-4 w-4 ${categoryInfo.colorClass} mt-0.5 flex-shrink-0`}
                                    />
                                    <div>
                                      <p className="text-xs font-medium text-white">
                                        {categoryInfo.label}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {categoryInfo.description}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Examples: {categoryInfo.examples}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Title *
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "title"
                              )}
                              className="bg-dark-3 border-dark-4 text-white"
                              maxLength={100}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/100
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-300">
                            Description
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={getCategoryPlaceholder(
                                form.watch("category"),
                                "description"
                              )}
                              className="bg-dark-3 border-dark-4 text-white min-h-[80px]"
                              maxLength={500}
                            />
                          </FormControl>
                          <p className="text-xs text-gray-400">
                            {field.value?.length || 0}/500
                          </p>
                          <FormMessage className="text-rose-400" />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{uploadStage}</span>
                      <span className="text-white">
                        {Math.floor(uploadProgress)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.floor(uploadProgress)}%`,
                        }}></div>
                    </div>
                    {uploadStage && (
                      <div className="text-xs text-gray-400">{uploadStage}</div>
                    )}
                  </div>
                )}

                {/* Post Button */}
                <Button
                  onClick={handleHybridUpload}
                  disabled={
                    !form.watch("title")?.trim() ||
                    isUploading ||
                    uploadVideoFile.status === "loading" ||
                    createVideoPost.status === "loading"
                  }
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white">
                  {isUploading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      Posting...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Post Business Video
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tip Modal */}
        {showTipModal && selectedVideo && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-sm">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-lg font-bold text-white">Send Tip</h2>
                <Button
                  onClick={() => {
                    setShowTipModal(false);
                    setSelectedVideo(null);
                    setTipAmount(10);
                    setTipMessage("");
                  }}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                <div className="text-center">
                  <img
                    src={selectedVideo.creator.imageUrl}
                    alt={selectedVideo.creator.name}
                    className="w-16 h-16 rounded-full mx-auto mb-2 object-cover"
                  />
                  <p className="text-white font-medium">
                    {selectedVideo.creator.name}
                  </p>
                  <p className="text-gray-400 text-sm">
                    @{selectedVideo.creator.username}
                  </p>
                  <p className="text-gray-300 text-xs mt-1 line-clamp-1">
                    {selectedVideo.title}
                  </p>
                </div>

                {/* Tip Amount Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Choose Amount
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[10, 25, 50, 100, 250, 500].map((amount) => (
                      <Button
                        key={amount}
                        onClick={() => setTipAmount(amount)}
                        className={`${
                          tipAmount === amount
                            ? "bg-primary-500 text-white"
                            : "bg-dark-3 text-gray-300 hover:bg-dark-4"
                        } rounded-lg py-2 text-sm font-medium`}>
                        ₹{amount}
                      </Button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <Input
                    type="number"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(Number(e.target.value))}
                    placeholder="Custom amount"
                    className="bg-dark-3 border-dark-4 text-white"
                    min="1"
                    max="10000"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Min: ₹1, Max: ₹10,000
                  </p>
                </div>

                {/* Optional Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message (Optional)
                  </label>
                  <Input
                    value={tipMessage}
                    onChange={(e) => setTipMessage(e.target.value)}
                    placeholder="Add a nice message..."
                    className="bg-dark-3 border-dark-4 text-white"
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {tipMessage.length}/100
                  </p>
                </div>

                {/* Payment Info */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <p className="text-green-200 text-sm">
                      Secure payment via Cashfree Gateway
                    </p>
                  </div>
                </div>

                {/* Send Tip Button */}
                <Button
                  onClick={handleSendTip}
                  disabled={tipAmount < 1 || isPaymentLoading}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 min-h-[48px]">
                  {isPaymentLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      <span>Processing Payment...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <IndianRupee className="h-5 w-5" />
                      <span className="text-base font-semibold">
                        Send ₹{tipAmount} Tip
                      </span>
                    </div>
                  )}
                </Button>

                {/* Terms */}
                <p className="text-xs text-gray-400 text-center">
                  By sending a tip, you agree to our terms and privacy policy
                </p>
              </div>
            </div>
          </div>
        )}

        {showGuidelinesModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-dark-2 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-dark-4">
                <h2 className="text-xl font-bold text-white">
                  Business Content Guidelines
                </h2>
                <Button
                  onClick={() => setShowGuidelinesModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                  variant="ghost">
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-4 space-y-4">
                {/* What's Allowed */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <h3 className="text-lg font-semibold text-white">
                      What Belongs on Bizz
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {CONTENT_GUIDELINES.allowed.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-sm text-gray-300">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What's Not Allowed */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-rose-500" />
                    <h3 className="text-lg font-semibold text-white">
                      What Doesn't Belong
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {CONTENT_GUIDELINES.notAllowed.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-sm text-gray-300">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Categories Preview */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Available Categories
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    {ENHANCED_BUSINESS_CATEGORIES.slice(0, 6).map(
                      (category) => {
                        const IconComponent = category.icon;
                        return (
                          <div
                            key={category.value}
                            className="flex items-center gap-3 p-2 bg-dark-3 rounded-lg">
                            <IconComponent
                              className={`h-4 w-4 ${category.colorClass}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-white">
                                {category.label}
                              </p>
                              <p className="text-xs text-gray-400">
                                {category.description}
                              </p>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                  {ENHANCED_BUSINESS_CATEGORIES.length > 6 && (
                    <p className="text-xs text-gray-400 mt-2">
                      ...and {ENHANCED_BUSINESS_CATEGORIES.length - 6} more
                      categories
                    </p>
                  )}
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-blue-200 text-sm">
                    <strong>Remember:</strong> Bizz is for business
                    professionals. Keep content valuable, educational, and
                    business-focused.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Loading State for Video Feed */}
        {isLoading && !feedData && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center">
              <Loader />
              <p className="text-white text-sm">Loading videos...</p>
            </div>
          </div>
        )}

        {/* No Videos State */}
        {!isLoading && videos.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center px-8">
              <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">
                No Business Videos Yet
              </h3>
              <p className="text-gray-400 text-sm mb-2">
                Be the first to share your business knowledge!
              </p>
              <p className="text-gray-500 text-xs mb-6">
                Share tutorials, showcase services, demonstrate skills, or tell
                your business story
              </p>

              <div className="flex gap-2">
                <Button
                  onClick={handleUploadClick}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2"
                  disabled={loadingKYC}>
                  {loadingKYC ? <Loader /> : <Upload className="h-5 w-5" />}
                </Button>

                <Button
                  onClick={handleRecordClick}
                  className="bg-primary-500 hover:bg-primary-600 text-white rounded-full p-2"
                  disabled={loadingKYC}>
                  {loadingKYC ? <Loader /> : <Plus className="h-6 w-6" />}
                </Button>
              </div>
              <Button
                onClick={() => setShowGuidelinesModal(true)}
                className="mt-4 text-gray-400 hover:text-white text-sm"
                variant="ghost">
                <Info className="h-4 w-4 mr-1" />
                View Content Guidelines
              </Button>
            </div>
          </div>
        )}

        {showKYCModal && (
          <KYCVerificationModal
            userId={user.id}
            onComplete={(status) => {
              setKycVerified(status.pan && status.bank);
              setShowKYCModal(false);

              if (status.pan && status.bank) {
                toast({
                  title: "KYC Completed!",
                  description:
                    "You can now upload videos and receive tips after 1 week",
                });
              } else {
                toast({
                  title: "KYC Partially Completed",
                  description: "Complete all verifications to upload videos",
                  variant: "destructive",
                });
              }
            }}
            onClose={() => setShowKYCModal(false)}
          />
        )}
      </div>
      {isPaymentLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            <Loader />
            <div className="text-center">
              <h3 className="font-semibold text-lg text-black">
                Processing Payment
              </h3>
              <p className="text-gray-600 text-sm">
                Please wait while we verify your payment...
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Bizz;
